import api, { unwrap } from './client';
import type { ApiResponse } from '@/types/api';

export interface MasterOption {
  code: string;
  label: string;
  extra?: string;
}

export interface TimezoneMasterOption {
  zoneId: string;
  label: string;
  displayName?: string;
  countryCode?: string;
  region?: string;
  utcOffsetMinutes?: number;
}

export interface CurrencyMasterOption {
  code: string;
  name: string;
  displayName?: string;
  symbol?: string;
  countryCode?: string;
  decimalPlaces?: number;
  numericCode?: string;
}

export interface TaxModeMasterOption {
  code: string;
  name: string;
  description?: string;
  countryCode?: string;
  defaultInclusive?: boolean;
  supportedTaxTypes?: string;
}

export type TimezoneCreatePayload = {
  zoneId: string;
  label: string;
  displayName?: string;
  countryCode?: string;
  region?: string;
};

export type CurrencyCreatePayload = {
  code: string;
  name: string;
  displayName?: string;
  symbol?: string;
  countryCode?: string;
  decimalPlaces?: number;
  numericCode?: string;
};

export type TaxModeCreatePayload = {
  code: string;
  name: string;
  description?: string;
  countryCode?: string;
  defaultInclusive?: boolean;
  supportedTaxTypes?: string;
};

export type MasterDataSearchParams = {
  search?: string;
  page?: number;
  size?: number;
};

export const masterDataApi = {
  countries: (params?: MasterDataSearchParams) =>
    unwrap(api.get<ApiResponse<MasterOption[]>>('/v1/master-data/countries', { params })),

  getCountry: (code: string) =>
    unwrap(api.get<ApiResponse<MasterOption>>(`/v1/master-data/countries/${encodeURIComponent(code)}`)),

  currencies: (params?: MasterDataSearchParams) =>
    unwrap(api.get<ApiResponse<MasterOption[]>>('/v1/master-data/currencies', { params })),

  currencyDetails: (params?: MasterDataSearchParams) =>
    unwrap(api.get<ApiResponse<CurrencyMasterOption[]>>('/v1/master-data/currencies/details', { params })),

  getCurrency: (code: string) =>
    unwrap(api.get<ApiResponse<MasterOption>>(`/v1/master-data/currencies/${encodeURIComponent(code)}`)),

  timezones: (params?: MasterDataSearchParams) =>
    unwrap(api.get<ApiResponse<MasterOption[]>>('/v1/master-data/timezones', { params })),

  timezoneDetails: (params?: MasterDataSearchParams) =>
    unwrap(api.get<ApiResponse<TimezoneMasterOption[]>>('/v1/master-data/timezones/details', { params })),

  getTimezone: (zoneId: string) =>
    unwrap(
      api.get<ApiResponse<MasterOption>>('/v1/master-data/timezones/lookup', {
        params: { zoneId },
      }),
    ),

  taxModes: (params?: MasterDataSearchParams) =>
    unwrap(api.get<ApiResponse<MasterOption[]>>('/v1/master-data/tax-modes', { params })),

  taxModeDetails: (params?: MasterDataSearchParams) =>
    unwrap(api.get<ApiResponse<TaxModeMasterOption[]>>('/v1/master-data/tax-modes/details', { params })),

  languages: () =>
    unwrap(api.get<ApiResponse<MasterOption[]>>('/v1/master-data/languages')),

  all: () =>
    unwrap(
      api.get<
        ApiResponse<{
          countries: MasterOption[];
          currencies: MasterOption[];
          timezones: MasterOption[];
          taxModes: MasterOption[];
          languages: MasterOption[];
        }>
      >('/v1/master-data/all'),
    ),

  addCountry: (code: string, name: string) =>
    unwrap(
      api.post<ApiResponse<MasterOption>>('/v1/master-data/countries', null, {
        params: { code, name },
      }),
    ),
  addCurrency: (payload: CurrencyCreatePayload) =>
    unwrap(api.post<ApiResponse<MasterOption>>('/v1/master-data/currencies', payload)),
  addTimezone: (payload: TimezoneCreatePayload) =>
    unwrap(api.post<ApiResponse<MasterOption>>('/v1/master-data/timezones', payload)),
  addTaxMode: (payload: TaxModeCreatePayload) =>
    unwrap(api.post<ApiResponse<MasterOption>>('/v1/master-data/tax-modes', payload)),
};
